/**
 * What a scanned QR is allowed to be, and what we do with it.
 *
 * `avengents pair` encodes a URL — `<base>/pair?code=<code>`, from deviceCli.ts:183,
 * where base defaults to https://aowl.tail371f08.ts.net:9443. So "scan the QR" ends in
 * a navigation, and that is exactly why this file exists.
 *
 * A QR CODE IS UNTRUSTED INPUT. It is a string a stranger can put on a sticker, and the
 * whole point of the scanner is that people point it at codes they did not generate. So
 * nothing here follows the scanned string. It is parsed, its host and path are checked
 * against what we expect, the code is pulled out, and the URL we navigate to is BUILT
 * FROM OUR OWN CONSTANTS with only the code carried across.
 *
 * The difference matters: `location.href = scanned` would send someone to whatever the
 * sticker said, signed in, on a screen that says "pairing". That is the phishing flow
 * this feature would otherwise be.
 */

/** The legacy approval origin — the :9443 site, where approvals lived before any
 * app origin could front /pair itself. Only approveUrl() may reach for it. */
const PAIR_ORIGIN = 'https://aowl.tail371f08.ts.net:9443'

/**
 * Where THIS page sends someone to approve a pairing.
 *
 * Same-origin by default — owner, 2026-08-13: 9445 的配對 link 無法使用. Every
 * origin that serves this app now fronts the pairing legs itself (/pair → the
 * pairing BFF; :9445 since today, the AWS domain when it comes), and a relative
 * URL means there is no second origin constant to drift. The ONE exception is
 * the legacy :9444 preview, whose proxy table is frozen by owner order (不可影響
 * 9444): it keeps sending approvals to the :9443 site, as it always has.
 *
 * Only `code` crosses over, re-encoded — a code containing `&admin=1` or a
 * fragment cannot add parameters (the load-bearing property the tests pin).
 */
export function approveUrl(code: string, loc: { port: string } = window.location): string {
  const base = loc.port === '9444' ? PAIR_ORIGIN : ''
  return `${base}/pair/approve/start?code=${encodeURIComponent(code)}`
}

/**
 * Hosts whose pairing links we will act on.
 *
 * A list rather than one string because the edge's base is overridable
 * (`AVENGENTS_BASE_URL`), so a machine pointed at a staging site prints a QR for that
 * site — and a scanner that silently refused it would look broken rather than careful.
 * Anything not on the list is REPORTED, not followed.
 *
 * THE SERVING ORIGIN'S OWN HOST leads the list, so this is origin-agnostic: on
 * app.avengents.ai an edge prints app.avengents.ai QRs, on the tailnet it prints
 * tailnet ones, and each is accepted where the app is actually served (S1, the
 * AWS migration — a hardcoded aowl host would have rejected every public QR). The
 * known dev hosts stay for the case where the app and the edge's base differ.
 */
const ALLOWED_HOSTS = [
  typeof window !== 'undefined' ? window.location.hostname : '',
  'aowl.tail371f08.ts.net',
].filter(Boolean)

export type ScanResult =
  | { kind: 'pair'; code: string; url: string }
  | { kind: 'not-a-pair-url'; saw: string }
  | { kind: 'wrong-host'; host: string }
  | { kind: 'no-code' }
  /** Typed text that is neither a link nor the XXXX-XXXX under the square. */
  | { kind: 'not-a-code'; saw: string }

/** The BFF's code alphabet, loosely — it re-validates; this only gates what we send. */
export const CODE_SHAPE = /^[A-Za-z0-9]{2,12}-[A-Za-z0-9]{2,12}$/

/**
 * What a person TYPED, judged — owner, 2026-09-13: 在 Scan pairing code 按鈕的右邊加上
 * 一個文字輸入欄位 (Enter pairing code). Two shapes are honest: the code itself — the
 * XXXX-XXXX printed under the square — or the whole link pasted. A link goes through
 * readPairQr's checks, the same host allowlist and the same built-from-constants
 * destination, because a pasted link is untrusted for exactly the reasons a scanned
 * one is. A bare code is upper-cased (the CLI prints it that way; a phone keyboard
 * does not) and only ever crosses into approveUrl re-encoded.
 */
export function readTypedPair(text: string): ScanResult {
  const raw = text.trim()
  if (!raw) return { kind: 'not-a-code', saw: '' }
  if (raw.includes('://')) return readPairQr(raw)
  const code = raw.toUpperCase()
  if (!CODE_SHAPE.test(code)) return { kind: 'not-a-code', saw: raw.slice(0, 60) }
  return { kind: 'pair', code, url: approveUrl(code) }
}

/**
 * The scanned string, judged.
 *
 * Returns a REASON on every rejection rather than null. A scanner that says only "that
 * did not work" is unusable in the one situation it exists for — a person holding a
 * phone at a screen, with no way to tell a wrong QR from a bad angle from a camera that
 * is focused on the wrong thing.
 */
export function readPairQr(scanned: string): ScanResult {
  const raw = scanned.trim()
  let u: URL
  try {
    u = new URL(raw)
  } catch {
    /*
     * Not a URL at all. Some pairing systems encode a bare code, and someone WILL try
     * this scanner on one, so the message has to say what was seen rather than assume
     * a malfunction. Truncated: a QR can hold ~4k characters and this ends up on screen.
     */
    return { kind: 'not-a-pair-url', saw: raw.slice(0, 60) }
  }
  /*
   * SCHEME FIRST, and my own test found why.
   *
   * `new URL("WIFI:S=cafe;T=WPA;P=hunter2;;")` SUCCEEDS — `WIFI:` is a valid scheme, and
   * a wifi-config QR is exactly the kind of code someone points this at by accident. It
   * parsed, came out with an empty hostname, and was reported as "that code points at ,
   * which is not this account's pairing site". A message with a hole where the host
   * should be, for a string that was never a web address at all.
   *
   * The same check is what refuses `javascript:` and `data:` before anything else looks
   * at them, which matters more than the wifi case: those are URLs whose hostname is
   * always empty, so a host allowlist alone never sees them.
   */
  if (u.protocol !== 'https:' && u.protocol !== 'http:')
    return { kind: 'not-a-pair-url', saw: raw.slice(0, 60) }
  // Host before path, because the wrong-host case is the dangerous one and it deserves
  // its own message rather than being flattened into "not a pairing link".
  if (!ALLOWED_HOSTS.includes(u.hostname)) return { kind: 'wrong-host', host: u.hostname }
  /*
   * THREE landing paths, because the one the QR prints is CONFIG, not a constant.
   *
   * The QR carries `{public_base_url}{post_login_path}?code=…` (bff app.py:735), and
   * post_login_path is HP_POST_LOGIN_PATH (bff config.py, default `/pair`): the
   * marketing site keeps `/pair`, the legacy server-issued link was `/devices`, and
   * app.avengents.ai serves the webapp at its ROOT — "the webapp wants its own route" —
   * so its QR is `<origin>/?code=…`. PairApprove mounts unconditionally and reads
   * `?code` path-agnostically (App.tsx), so every one of these is a real pairing URL.
   *
   * Twice a missing entry here refused every real code: first `/devices` (the check was
   * built around the `/pair` fallback, never the server-issued path), then `/` (the
   * owner scanned the live app and got "it says /"). The list is the landing paths and
   * ONLY those — `/logout?code=` on our own origin is still refused: the URL we open is
   * built from constants, so a non-landing path has no business carrying a pairing code.
   */
  if (!/^\/(pair|devices)?\/?$/.test(u.pathname))
    return { kind: 'not-a-pair-url', saw: u.pathname }

  const code = u.searchParams.get('code')?.trim()
  if (!code) return { kind: 'no-code' }

  /*
   * The destination is built here, not taken from the scan. Only `code` crosses over,
   * and it crosses re-encoded — so a code containing `&admin=1` or a fragment cannot
   * add parameters to the URL we navigate to.
   *
   * /pair/approve/start is the browser half of the flow (homepage BFF app.py:520). Its
   * own comment is the reason this scanner is worth having: "A device-issued ticket
   * arrives with no email — the machine could not know who would scan its QR. Whoever
   * approves becomes the account."
   */
  return {
    kind: 'pair',
    code,
    url: approveUrl(code),
  }
}

/** What to put on screen for a rejection. Says what was wrong AND what to do. */
export function scanProblem(r: Exclude<ScanResult, { kind: 'pair' }>): string {
  switch (r.kind) {
    case 'wrong-host':
      return `That code points at ${r.host}, which is not this account's pairing site — not opening it.`
    case 'no-code':
      return 'That is a pairing link, but it carries no code. The one on screen may have expired — the machine prints a fresh one every few minutes.'
    case 'not-a-pair-url':
      return `That is not a pairing code — it says “${r.saw}”. Run \`avengents pair\` on the machine and scan the square it prints.`
    case 'not-a-code':
      return r.saw
        ? `“${r.saw}” is not a pairing code — it looks like XXXX-XXXX, printed under the square by \`avengents pair\`.`
        : 'Type the code printed under the square by `avengents pair` — it looks like XXXX-XXXX.'
  }
}
